<!-- Slide for displaying funeral over time budget based on inflation rate -->
<template>
	<div class="h-full overflow-y-auto px-10 pt-14">
		<div
			v-for="(plan, index) in plans"
			class="border-b border-slate-200 p-5 last:border-b-0"
			:key="plan.id"
		>
			<div class="mx-5 my-3 flex items-center">
				<PlanAvatar
					:plan="plan"
					:index="index"
					large
					filled
				/>
				<div class="text-secondary-900 ml-3 text-2xl font-bold">
					{{ planNames![index] }}'s
					{{ t('inflationEstimateSlide.funeralOverTime') }}
				</div>
			</div>
			<div class="m-5 flex flex-col gap-9 md:flex-row">
				<div class="w-full md:w-1/2">
					<BlobImage
						class="h-auto w-full rounded-xl object-cover"
						:src="planImages![index]"
						:alt="plan.lastName"
					/>
				</div>
				<div class="w-full md:w-1/2">
					<!-- Inflation Graph -->
					<div
						class="fluid relative aspect-1256/600 w-full overflow-hidden rounded-2xl shadow-sm"
					>
						<img
							src="../../assets/inflation-graph.svg"
							alt="Inflation Graph"
							class="absolute inset-0 h-full w-full object-cover"
						/>

						<!-- Today's Cost -->
						<div class="absolute bottom-[15%] left-[7%] text-center uppercase">
							<div class="text-[10px] font-bold text-slate-500">
								{{ t('inflationEstimateSlide.today') }}
							</div>
							<div class="text-primary-700 text-lg font-bold">
								{{ getTodayPrice(index) }}
							</div>
						</div>

						<!-- Future Cost -->
						<div
							class="absolute right-[10%] bottom-[45%] flex flex-col items-center"
						>
							<div
								class="flex items-center justify-end gap-1 text-[10px] font-bold text-slate-500 uppercase"
							>
								<span
									>{{ lastYear }} {{ t('inflationEstimateSlide.years') }}</span
								>
							</div>
							<div class="text-primary-700 flex items-center gap-1 font-bold">
								<PlayIcon
									class="-rotate-90 text-red-500 sm:size-3 md:size-4 lg:size-5"
								/><span class="text-lg">{{ getFuturePrice(index) }}</span>
							</div>
						</div>
					</div>

					<!-- Inflation Breakdown -->
					<div>
						<div class="my-4 flex justify-between">
							<div class="text-secondary-900 font-semibold">
								{{ t('inflationEstimateSlide.todayCost') }}
							</div>
							<div class="text-md text-primary-600 font-medium">
								{{ formatPrice(planPrice![index].grandTotal) }}
							</div>
						</div>
						<div
							v-for="years in yearSteps"
							:key="years"
							class="my-4 flex justify-between"
						>
							<div class="text-sm font-semibold text-red-600 uppercase">
								{{ years }} {{ t('inflationEstimateSlide.years') }}
							</div>
							<div class="text-secondary-900 text-sm font-medium">
								{{
									formatPrice(
										calculateInflationEstimate(
											planPrice![index].grandTotal,
											years,
											inflationRate
										)
									)
								}}
							</div>
						</div>
					</div>

					<div
						class="text-secondary-400 justify-self-end text-[10px] font-medium uppercase italic"
					>
						{{
							t('inflationEstimateSlide.assumedInflationRate', {
								rate: inflationRate,
							})
						}}
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import BlobImage from '../common/BlobImage.vue'
import PlanAvatar from '../presentation/PlanAvatar.vue'
import { usePresentationLocale } from '@/i18n'
import { useMerchandise } from '@/mixins/merchandise'
import type { InflationEstimateSlideStoryblok } from '@/sbTypes'
import { useSessionStore } from '@/stores/session'
import { useUserSettingsStore } from '@/stores/settings'
import { PlayIcon } from '@heroicons/vue/24/solid'
import { Swiper } from 'swiper'
import { computed, type PropType } from 'vue'

const settingsStore = useUserSettingsStore()
settingsStore.loadSettings() // optional onMounted

const { t } = usePresentationLocale()
const props = defineProps({
	blok: Object as PropType<InflationEstimateSlideStoryblok>,
	parentSwiper: Swiper,
	active: Boolean,
})

const sessionStore = useSessionStore()
const {
	getPlanImage,
	calculateCartTotals,
	formatPrice,
	calculateInflationEstimate,
} = useMerchandise()

// Get the last year step from the year steps. If it doesn't exist, default to 20.
const lastYear = computed(() => yearSteps.value.at(-1) ?? 20)

// Get today's price using the current grand total
const getTodayPrice = (index: number) => {
	return formatPrice(planPrice.value?.[index]?.grandTotal ?? 0)
}

// Get future price for the graph using the last year step and the inflation rate
const getFuturePrice = (index: number) => {
	const base = planPrice.value?.[index]?.grandTotal ?? 0
	return formatPrice(
		calculateInflationEstimate(base, lastYear.value, inflationRate.value)
	)
}

// Get plans
const plans = computed(() => {
	return sessionStore.activePlans
})

// Get year increments from the store if they exist or default to 5 if they don't
const yearIncrements = computed(() => {
	return settingsStore.values.inflationYearIncrements ?? 5
})

// Get 4 year steps based on yearIncrements
const yearSteps = computed(() => {
	return [1, 2, 3, 4].map((multiplier) => multiplier * yearIncrements.value)
})

// Get inflation rate from the store if it exists or the storyblok setting if it doesn't
const inflationRate = computed(() => {
	const storeRate = settingsStore.values.inflationRate
	const blokRate = parseFloat(props.blok?.rate ?? '0')
	return storeRate !== undefined ? storeRate : blokRate
})

// Get plan names
const planNames = computed(() => {
	return (
		plans.value?.map((plan) => {
			return [plan.firstName, plan.lastName].filter((p) => !!p).join(' ')
		}) ?? []
	)
})

// Get plan prices
const planPrice = computed(() => {
	return plans.value?.map((plan) => calculateCartTotals(plan))
})

// Get plan images
const planImages = computed(() => {
	return plans.value?.map((plan) => getPlanImage(plan.id))
})
</script>
